import { useContext, useEffect, useState } from 'react';
import { Paper, Box, Grid, Typography } from '@mui/material';
import { PersonOutlineOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../AppContext';
import { SttDialog, SttFormText, SttFormDate, SttFormSelect } from '../all';
import { subsetObject, formatPriceByLocale } from '../../lib/utils';
import Mship from '../../pages/sc/mships/Mship';
import DateBuilder from '../../lib/DateBuilder';

type MshipData = {
    id: number;
    name: string;
    address?: string;
    dni?: string;
    email?: string;
    mobile?: string;
    birthday?: string;
    gender?: 'MALE' | 'FEMALE';
    iban?: string | null;
    created?: string;
    amount?: number;
    amountShape?: {
        currency?: string;
        locale?: string;
    };
    total?: number;
    totalShape?: {
        currency?: string;
        locale?: string;
    };
    debt?: number;
    debtShape?: {
        currency?: string;
        locale?: string;
    };
    user: {
        login: string;
    };
};

type Props = {
    open: boolean;
    mshipId?: number | null;
    onClose: () => void;
    onSave?: (mship: MshipData) => void;
};

export default function SttMshipDialog({
    open,
    mshipId,
    onClose,
    onSave,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;
    const [mship, setMship] = useState<MshipData | undefined>();
    const [fullProfileId, setFullProfileId] = useState<number | null>(null);

    function loadMship(id: number) {
        cxt?.api('GET', `/mships/${id}`, {
            success: (r: { mship: MshipData }) => {
                setMship(r.mship);
            },
        });
    }

    useEffect(() => {
        if (mshipId) {
            loadMship(mshipId);
        }
    }, [mshipId]);

    function handleChange({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) {
        if (!name) return;
        setMship((prev: any) => ({ ...prev, [name]: value }) as MshipData);
    }

    function saveMship() {
        if (!mship) return;

        const params = subsetObject(
            mship,
            'name,address,dni,email,mobile,birthday,gender,iban',
        );

        params.iban = params.iban !== '' ? params.iban : null;

        cxt?.api('PUT', `/mships/${mship.id}`, {
            params: {
                ...params,
            },
            success: (response: { mship: MshipData }) => {
                onClose();
                onSave?.(response.mship);
            },
        });
    }

    function openFullMship(id: number) {
        setFullProfileId(id);
    }

    // -----| Render |-----
    return (
        <>
            <SttDialog
                title={mship ? `@${mship.user.login} - ${mship.name}` : ''}
                open={open}
                onClose={onClose}
                content={
                    mship && (
                        <>
                            <Paper>
                                <Box p={2}>
                                    <Grid container spacing={3}>
                                        <Grid size={{ xs: 3 }}>
                                            {t('Created')}
                                        </Grid>
                                        <Grid size={{ xs: 3 }}>
                                            {t('Purse')}
                                        </Grid>
                                        <Grid size={{ xs: 3 }}>
                                            {t('Total')}
                                        </Grid>
                                        <Grid size={{ xs: 3 }}>
                                            {t('Debt')}
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={3}>
                                        <Grid size={{ xs: 3 }}>
                                            <Typography
                                                variant="subtitle2"
                                                gutterBottom
                                            >
                                                {new DateBuilder(
                                                    mship.created,
                                                    cxt?.sc?.timezone?.name,
                                                    cxt?.lang?.key,
                                                ).dmy()}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 3 }}>
                                            <Typography
                                                variant="subtitle2"
                                                gutterBottom
                                            >
                                                {formatPriceByLocale(
                                                    mship.amount,
                                                    mship?.amountShape
                                                        ?.currency,
                                                    mship?.amountShape?.locale,
                                                )}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 3 }}>
                                            <Typography
                                                variant="subtitle2"
                                                gutterBottom
                                            >
                                                {formatPriceByLocale(
                                                    mship.total,
                                                    mship?.totalShape?.currency,
                                                    mship?.totalShape?.locale,
                                                )}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 3 }}>
                                            <Typography
                                                variant="subtitle2"
                                                gutterBottom
                                            >
                                                {formatPriceByLocale(
                                                    mship.debt,
                                                    mship?.debtShape?.currency,
                                                    mship?.debtShape?.locale,
                                                )}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Paper>
                            <Paper>
                                <Box p={2} mt={2}>
                                    <Grid container spacing={3}>
                                        <Grid size={{ xs: 12 }}>
                                            <SttFormText
                                                name="name"
                                                caption={t('Name')}
                                                defVal={mship.name}
                                                onChange={handleChange}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 8 }}>
                                            <SttFormText
                                                name="address"
                                                caption={t('Address')}
                                                defVal={mship.address}
                                                onChange={handleChange}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 4 }}>
                                            <SttFormText
                                                name="dni"
                                                caption={t('DNI')}
                                                defVal={mship.dni}
                                                onChange={handleChange}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 8 }}>
                                            <SttFormText
                                                name="email"
                                                caption={t('Email')}
                                                defVal={mship.email}
                                                onChange={handleChange}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 4 }}>
                                            <SttFormText
                                                name="mobile"
                                                caption={t('Mobile')}
                                                defVal={mship.mobile}
                                                onChange={handleChange}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 6 }}>
                                            <SttFormDate
                                                name="birthday"
                                                caption={t('Birthday')}
                                                defVal={mship.birthday}
                                                onChange={handleChange}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 6 }}>
                                            <SttFormSelect
                                                name="gender"
                                                caption={t('Gender')}
                                                defVal={mship.gender}
                                                options={[
                                                    {
                                                        caption: t(`Male`),
                                                        value: 'MALE',
                                                    },
                                                    {
                                                        caption: t(`Female`),
                                                        value: 'FEMALE',
                                                    },
                                                ]}
                                                onChange={handleChange}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12 }}>
                                            <SttFormText
                                                name="iban"
                                                caption={t('IBAN')}
                                                defVal={mship.iban}
                                                onChange={handleChange}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Paper>
                        </>
                    )
                }
                buttons={[
                    {
                        type: 'save',
                        onClick: saveMship,
                    },
                    {
                        icon: <PersonOutlineOutlined />,
                        onClick: () => {
                            if (mship) {
                                openFullMship(mship.id);
                            }
                        },
                        caption: t('Profile'),
                    },
                ]}
            />
            {fullProfileId && (
                <Mship
                    open={!!fullProfileId}
                    id={fullProfileId}
                    onClose={() => {
                        loadMship(fullProfileId);
                        setFullProfileId(null);
                    }}
                    disableUrlNavigation
                />
            )}
        </>
    );
}
